import { formatDateAndTime } from 'utils/stringUtils'
import { NonCapturedPaymentDto, PaymentOpsPaymentEventDto } from '../../../../generated-api'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import Payload from 'components/Payload/Payload'
import { ChevronLeftIcon, ExternallinkIcon } from '@ordr-as/icons'
import { Button } from '@ordr-as/umami-react'
import { usePaymentOperations } from '../context/PaymentOperationsContext'
import { getReason } from './NonCapturedPaymentsUtils'
import { useGetPayment } from '../../../api/paymentApi'
import config from '../../../config'

type NonCapturedPaymentDetailsProps = {
    nonCapturedPayments: NonCapturedPaymentDto[]
}
export const NonCapturedPaymentDetails = ({ nonCapturedPayments }: NonCapturedPaymentDetailsProps) => {
    const [selectedEvent, setSelectedEvent] = useState<PaymentOpsPaymentEventDto>()
    const { paymentReference } = useParams<{ paymentReference: string }>()
    const payment = nonCapturedPayments.find((p) => p.paymentReference === paymentReference)
    const [isRetrying, setIsRetrying] = useState<boolean>()

    const { retryNonCaptured, excludeFromWashing } = usePaymentOperations()
    const { data: paymentDetails } = useGetPayment(paymentReference!)

    const reason = getReason(payment)

    const retryPayment = () => {
        if (payment) {
            setIsRetrying(true)
            retryNonCaptured([payment.paymentId])

            setTimeout(() => {
                setIsRetrying(false)
            }, 30000)
        }
    }

    const excludePayment = () => {
        const sure = confirm(
            "Are you sure you want to exclude this payment from washing? If you're not sure, ask a friend."
        )

        if (!sure || !payment) {
            return
        }

        excludeFromWashing([payment.paymentId])
    }

    if (!payment) {
        return <Navigate to="/payment-operations/psp-notifications" />
    }

    const canRetry = payment.latestState === 'CAPTURING' || payment.latestState === 'AUTHORISED'

    return (
        <div>
            <div className="rounded-lg bg-brand-white shadow-lg py-4 mt-4 px-5">
                <Link
                    to="../non-captured-payments"
                    className="flex items-center text-[#3b82f6] text-sm cursor-pointer pb-4"
                >
                    <ChevronLeftIcon className="mr-2" />
                    Back to list
                </Link>
                <h2 className="text-lg font-semibold pb-2">Payment Details</h2>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div className="font-semibold border-t border-grey-lightest">Payment ID:</div>
                    <div className="border-t border-grey-lightest">{payment.paymentId}</div>
                    <div className="font-semibold border-t border-grey-lightest">Reference:</div>
                    <div className="border-t border-grey-lightest">
                        {payment.paymentReference ? (
                            <Link to={`/payments/${payment.paymentReference}`} className="text-[#3b82f6]">
                                {payment.paymentReference}
                            </Link>
                        ) : (
                            payment.paymentReference
                        )}
                    </div>
                    <div className="font-semibold border-t border-grey-lightest">Latest State:</div>
                    <div className="border-t border-grey-lightest">{payment.latestState}</div>
                    <div className="font-semibold border-t border-grey-lightest">Reason:</div>
                    <div className="border-t border-grey-lightest">{reason}</div>
                    <div className="font-semibold border-t border-grey-lightest">Latest Event Code:</div>
                    <div className="border-t border-grey-lightest"> {payment.latestEventCode}</div>
                    <div className="font-semibold border-t border-grey-lightest">Payment Provider:</div>
                    <div className="border-t border-grey-lightest">{payment.paymentProvider}</div>
                    <div className="font-semibold border-t border-grey-lightest">Payment Method:</div>
                    <div className="border-t border-grey-lightest">{payment.paymentMethod}</div>
                    <div className="font-semibold border-t border-grey-lightest">Location ID:</div>
                    <div className="border-t border-grey-lightest">{payment.executingLocationId}</div>
                    <div className="font-semibold border-t border-grey-lightest">Created:</div>
                    <div className="border-t border-grey-lightest">{formatDateAndTime(payment.created)}</div>
                    <div className="font-semibold border-t border-grey-lightest">Updated:</div>
                    <div className="border-t border-grey-lightest">{formatDateAndTime(payment.updated)}</div>
                    <div className="font-semibold">Action:</div>
                    <div className="col-span-2 landscape:col-span-1 flex items-center">
                        {canRetry ? (
                            <Button onClick={retryPayment} isDisabled={!canRetry || isRetrying} kind="primary">
                                Retry
                            </Button>
                        ) : (
                            <span className="text-grey-dark">No action available</span>
                        )}
                        <Button
                            kind="tertiary"
                            className="ml-2"
                            onClick={excludePayment}
                            isDisabled={!payment.canExcludeFromWashing}
                        >
                            Exclude from washing
                        </Button>
                    </div>
                </div>
            </div>
            {(paymentDetails?.orderReference ||
                (paymentDetails?.pspReference && payment.paymentProvider === 'ADYEN')) && (
                <div className="rounded-lg bg-brand-white shadow-lg py-4 mt-4 px-5 flex gap-3">
                    {paymentDetails?.orderReference && (
                        <Button
                            kind="primary"
                            onClick={() =>
                                window.open(
                                    `${config.adminBaseUrl}/#/location/${payment.executingLocationId}/order-details/${paymentDetails.orderReference}`,
                                    '_blank',
                                    'noreferrer'
                                )
                            }
                        >
                            See order in admin
                            <ExternallinkIcon className="ml-2 text-xs inline align-baseline" />
                        </Button>
                    )}
                    {paymentDetails?.pspReference && payment.paymentProvider === 'ADYEN' && (
                        <Button
                            kind="primary"
                            className="!bg-green-600 hover:!bg-green-700"
                            onClick={() =>
                                window.open(
                                    `${config.adyen.caBaseUrl}/ca/ca/accounts/showTx.shtml?pspReference=${paymentDetails.pspReference}&txType=Payment`,
                                    '_blank',
                                    'noreferrer'
                                )
                            }
                        >
                            See payment in Adyen
                            <ExternallinkIcon className="ml-2 text-xs inline align-baseline" />
                        </Button>
                    )}
                </div>
            )}
            <div className="flex flex-col desktop:flex-row rounded-lg rounded-tl-none bg-brand-white shadow-lg py-4">
                <>
                    {selectedEvent && (
                        <section className="flex-1 desktop:w-6/12 desktop:order-last border-b-2 desktop:border-0 border-brand-primary desktop:border-grey-lightest pb-3 desktop:pb-0">
                            <Payload event={selectedEvent} />
                        </section>
                    )}
                    <section className="flex-1 desktop:border-r border-grey-lightest desktop:w-6/12 pt-3 desktop:pt-0 overflow-x-auto">
                        <div className="min-w-full">
                            <table className="text-left overflow-hidden w-full">
                                <thead className="text-grey-dark">
                                    <tr>
                                        <th scope="col" className="font-normal py-2 pl-6">
                                            Event code
                                        </th>
                                        <th scope="col" className="font-normal py-2 px-2">
                                            New state
                                        </th>
                                        <th scope="col" className="font-normal py-2 pr-2">
                                            Created
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payment.events.map((event, idx) => (
                                        <tr
                                            onClick={() => setSelectedEvent(event)}
                                            key={idx}
                                            className={`hover:bg-purple-lightest cursor-pointer border-t border-grey-lightest ${
                                                event === selectedEvent && 'bg-purple-lightest'
                                            }`}
                                        >
                                            <th
                                                scope="row"
                                                className="text-brand-primary py-4 pl-6 whitespace-nowrap text-sm"
                                            >
                                                {event.eventCode}
                                            </th>
                                            <td className="py-4 whitespace-nowrap text-sm px-2">{event.newState}</td>
                                            <td className="py-4 whitespace-nowrap text-sm pr-2">
                                                {formatDateAndTime(event.created?.toString() ?? '')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            </div>
        </div>
    )
}
